import { useEffect } from "react";
import * as THREE from "three";
import * as SkeletonUtils from "three/addons/utils/SkeletonUtils.js";

export default function UI3DModelButtons({
  scene,
  camera,
  rendererRef,
  modelTemplates,
  onClickHandlerRef,
  changeScenarioRef,
  boardSizeRef,
  GRID_SIZE,
}) {
  const template = modelTemplates?.arrow;
  const dimZ = boardSizeRef?.dimZ;

  useEffect(() => {
    const dom = rendererRef?.current?.domElement || document.querySelector("canvas");
    if (!scene || !camera || !template || !dimZ || !GRID_SIZE || !dom) return;

    const groups = [];
    const geometries = new Set();
    const materials = new Set();
    const textures = new Set();
    const colors = new Map();
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const hoverColor = new THREE.Color(0xffaa00);
    let hovered = null;
    let frame;

    function ownGeometry(geometry) {
      geometries.add(geometry);
      return geometry;
    }
    function ownMaterial(material, tinted = false) {
      materials.add(material);
      if (tinted && material.color) colors.set(material, material.color.clone());
      return material;
    }

    function createLabel(text, color) {
      // Etykieta nie wymaga pobierania pliku TTF.
      const canvas = document.createElement("canvas");
      canvas.width = 1024;
      canvas.height = 256;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.font = "bold 100px Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "white";
      ctx.fillText(text, 512, 128, 980);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      textures.add(texture);
      const mesh = new THREE.Mesh(
        ownGeometry(new THREE.PlaneGeometry(180, 45)),
        ownMaterial(new THREE.MeshBasicMaterial({
          map: texture, color, transparent: true, depthWrite: false,
          side: THREE.DoubleSide,
        }), true),
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = -Math.PI / 2;
      return mesh;
    }

    function addButton(z, rotationY, direction, label) {
      const baseColor = 0x294e6b;
      const group = new THREE.Group();
      group.userData.isUIGroup = true;
      group.userData.targetScale = 1;
      group.userData.onClick = () => changeScenarioRef?.current?.(direction);

      const model = SkeletonUtils.clone(template);
      model.traverse(child => {
        if (!child.isMesh) return;
        // clone() modelu współdzieli geometrię; UI posiada własne kopie.
        child.geometry = ownGeometry(child.geometry.clone());
        const cloneMaterial = original => {
          const material = original.clone();
          material.color?.set(baseColor);
          return ownMaterial(material, true);
        };
        child.material = Array.isArray(child.material)
          ? child.material.map(cloneMaterial) : cloneMaterial(child.material);
      });
      // Wrapper zachowuje transformacje oryginalnego glTF.
      const pivot = new THREE.Group();
      pivot.add(model);
      pivot.rotation.y = rotationY;
      pivot.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(pivot);
      if (box.isEmpty()) {
        console.warn("UI3DModelButtons: model arrow nie zawiera geometrii");
        return;
      }
      const size = box.getSize(new THREE.Vector3());
      pivot.scale.setScalar(90 / Math.max(size.x, size.y, size.z, 0.000001));
      pivot.updateMatrixWorld(true);
      box.setFromObject(pivot);
      const center = box.getCenter(new THREE.Vector3());
      pivot.position.set(-center.x, 5 - box.min.y, -center.z);
      group.add(pivot);

      const hitbox = new THREE.Mesh(
        ownGeometry(new THREE.PlaneGeometry(180, 260)),
        ownMaterial(new THREE.MeshBasicMaterial({
          transparent: true, opacity: 0, depthWrite: false,
          colorWrite: false, side: THREE.DoubleSide,
        })),
      );
      hitbox.rotation.x = -Math.PI / 2;
      hitbox.position.set(0, 1, direction * 65);
      group.add(hitbox);

      const text = createLabel(label, baseColor);
      if (text) {
        text.position.set(0, 2, direction * 150);
        group.add(text);
      }
      group.position.set(0, 0, z);
      scene.add(group);
      group.updateMatrixWorld(true);
      groups.push(group);
    }

    addButton(50, -Math.PI / 2, 1, "NASTĘPNY");
    addButton(-dimZ * GRID_SIZE - 50, Math.PI / 2, -1, "POPRZEDNI");

    function pick(event) {
      const rect = dom.getBoundingClientRect();
      if (!rect.width || !rect.height || event.clientX < rect.left ||
          event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return null;
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      camera.updateWorldMatrix(true, false);
      groups.forEach(group => group.updateWorldMatrix(true, true));
      raycaster.setFromCamera(pointer, camera);
      let object = raycaster.intersectObjects(groups, true)[0]?.object;
      while (object && !object.userData.isUIGroup) object = object.parent;
      return object || null;
    }
    function setHovered(next) {
      if (hovered) hovered.userData.targetScale = 1;
      hovered = next;
      if (hovered) hovered.userData.targetScale = 1.15;
    }
    const onMove = event => setHovered(pick(event));
    const onLeave = () => setHovered(null);
    const onClick = event => {
      if (event.button !== undefined && event.button !== 0) return false;
      const group = pick(event);
      if (!group) return false;
      group.userData.onClick();
      return true;
    };
    if (onClickHandlerRef) onClickHandlerRef.current = onClick;
    dom.addEventListener("pointermove", onMove);
    dom.addEventListener("pointerleave", onLeave);

    function animate() {
      for (const group of groups) {
        const scale = THREE.MathUtils.lerp(group.scale.x, group.userData.targetScale, 0.15);
        group.scale.setScalar(scale);
        const t = THREE.MathUtils.clamp((scale - 1) / 0.15, 0, 1);
        group.traverse(child => {
          if (!child.isMesh) return;
          const list = Array.isArray(child.material) ? child.material : [child.material];
          list.forEach(material => {
            const base = colors.get(material);
            if (base) material.color.lerpColors(base, hoverColor, t);
          });
        });
      }
      frame = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      cancelAnimationFrame(frame);
      dom.removeEventListener("pointermove", onMove);
      dom.removeEventListener("pointerleave", onLeave);
      if (onClickHandlerRef?.current === onClick) onClickHandlerRef.current = null;
      groups.forEach(group => scene.remove(group));
      geometries.forEach(geometry => geometry.dispose());
      materials.forEach(material => material.dispose());
      textures.forEach(texture => texture.dispose());
    };
  }, [scene, camera, rendererRef, template, dimZ, GRID_SIZE, changeScenarioRef, onClickHandlerRef]);

  return null;
}




// import { useEffect, useState, useRef } from "react";
// import * as THREE from "three";
// import { TTFLoader } from "three/examples/jsm/loaders/TTFLoader.js";
// import { Font } from "three/examples/jsm/loaders/FontLoader.js";
// import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";

// export default function UI3DModelButtons({
//   scene,
//   camera,
//   modelTemplates,
//   onClickHandlerRef,
//   changeScenarioRef,
//   boardSizeRef,
//   GRID_SIZE,
// }) {
//   const [font, setFont] = useState(null);
//   const hoveredRef = useRef(null);
//   const animationRef = useRef(null);

//   useEffect(() => {
//     const loader = new TTFLoader();
//     loader.load("/fonts/Roboto.ttf", (json) => {
//       setFont(new Font(json));
//     });
//   }, []);

//   useEffect(() => {
//     if (!scene || !camera || !modelTemplates || !boardSizeRef || !font) return;

//     const uiGroups = [];
//     const { dimZ } = boardSizeRef;
//     if (!dimZ) return;

//     const margin = 50;
//     const centerZ = -(dimZ / 2) * GRID_SIZE;

//     function clearUI() {
//       uiGroups.forEach((group) => {
//         scene.remove(group);
//         group.traverse((child) => {
//           if (child.geometry) child.geometry.dispose();
//           if (child.material) child.material.dispose();
//         });
//       });
//       uiGroups.length = 0;
//       hoveredRef.current = null;
//     }

//     function animate() {
//       uiGroups.forEach((group) => {
//         const target = group.userData.targetScale || 1;
//         const current = group.scale.x;

//         const next = THREE.MathUtils.lerp(current, target, 0.15);
//         group.scale.set(next, next, next);

//         group.traverse((child) => {
//           if (child.isMesh && child.material?.color) {
//             const base = new THREE.Color(child.userData.baseColor);
//             const hover = new THREE.Color(0xffaa00);

//             const t = (next - 1) / 0.15;
//             child.material.color.lerpColors(base, hover, Math.max(0, t));
//           }
//         });
//       });

//       animationRef.current = requestAnimationFrame(animate);
//     }

//     animate();

//     function handlePointerMove(event) {
//       const dom =
//         camera?.renderer?.domElement || document.querySelector("canvas");
//       if (!dom) return;

//       const rect = dom.getBoundingClientRect();

//       const pointer = new THREE.Vector2(
//         ((event.clientX - rect.left) / rect.width) * 2 - 1,
//         -((event.clientY - rect.top) / rect.height) * 2 + 1,
//       );

//       const raycaster = new THREE.Raycaster();
//       raycaster.setFromCamera(pointer, camera);

//       const intersects = raycaster.intersectObjects(uiGroups, true);

//       if (!intersects.length) {
//         if (hoveredRef.current) {
//           hoveredRef.current.userData.targetScale = 1;
//           hoveredRef.current = null;
//         }
//         return;
//       }

//       let obj = intersects[0].object;
//       while (obj && !obj.userData.isUIGroup) obj = obj.parent;
//       if (!obj) return;

//       if (hoveredRef.current !== obj) {
//         if (hoveredRef.current) hoveredRef.current.userData.targetScale = 1;

//         obj.userData.targetScale = 1.15;
//         hoveredRef.current = obj;
//       }
//     }

//     function addUIModelButton(
//       modelName,
//       offsetZ,
//       rotationY,
//       onClick,
//       label,
//       baseColor = 0x294e6b,
//     ) {
//       const tpl = modelTemplates[modelName];
//       if (!tpl) return;

//       const group = new THREE.Group();
//       group.userData.isUIGroup = true;
//       group.userData.onClick = onClick;
//       group.userData.targetScale = 1;

//       //STREFA rozmiar
//       const hitGeo = new THREE.PlaneGeometry(150, 300);
//       const hitMat = new THREE.MeshBasicMaterial({
//         transparent: true,
//         opacity: 0,
//       });
//       const hitbox = new THREE.Mesh(hitGeo, hitMat);
//       hitbox.rotation.x = -Math.PI / 2;
//       const outwardOffset = 100;
//       hitbox.position.z = outwardOffset * Math.sign(offsetZ || 1);
//       group.add(hitbox);

//       const model = tpl.clone(true);
//       model.traverse((child) => {
//         if (child.isMesh) {
//           child.material = child.material.clone();
//           child.material.color.set(baseColor);
//           child.userData.baseColor = baseColor;
//         }
//       });

//       model.scale.set(30, 30, 30);
//       model.rotation.y = rotationY;
//       group.add(model);

//       if (label) {
//         const outwardOffset = 150;
//         const textOffsetZ = outwardOffset * Math.sign(offsetZ || 1);

//         const textGeo = new TextGeometry(label, {
//           font,
//           size: 5,
//           height: 1,
//         });

//         textGeo.computeBoundingBox();
//         textGeo.center();

//         const textMat = new THREE.MeshStandardMaterial({
//           color: baseColor,
//         });

//         const textMesh = new THREE.Mesh(textGeo, textMat);
//         textMesh.position.set(0, 0.1, textOffsetZ);
//         textMesh.rotation.x = -Math.PI / 2;
//         textMesh.rotation.z = -Math.PI / 2;
//         textMesh.scale.set(4, 4, 0.05);

//         textMesh.userData.baseColor = baseColor;

//         group.add(textMesh);
//       }

//       group.position.set(0, 0, centerZ + offsetZ);

//       scene.add(group);
//       uiGroups.push(group);
//     }

//     clearUI();

//     addUIModelButton(
//       "arrow",
//       (dimZ / 2) * GRID_SIZE + margin,
//       -Math.PI / 2,
//       () => changeScenarioRef?.current?.(1),
//       "NASTĘPNY",
//     );

//     addUIModelButton(
//       "arrow",
//       (-dimZ / 2) * GRID_SIZE - margin,
//       Math.PI / 2,
//       () => changeScenarioRef?.current?.(-1),
//       "POPRZEDNI",
//     );

//     if (onClickHandlerRef) {
//       onClickHandlerRef.current = (event) => {
//         const dom =
//           camera?.renderer?.domElement || document.querySelector("canvas");
//         if (!dom) return false;

//         const rect = dom.getBoundingClientRect();

//         const pointer = new THREE.Vector2(
//           ((event.clientX - rect.left) / rect.width) * 2 - 1,
//           -((event.clientY - rect.top) / rect.height) * 2 + 1,
//         );

//         const raycaster = new THREE.Raycaster();
//         raycaster.setFromCamera(pointer, camera);

//         const intersects = raycaster.intersectObjects(uiGroups, true);
//         if (!intersects.length) return false;

//         let obj = intersects[0].object;
//         while (obj && !obj.userData.isUIGroup) obj = obj.parent;

//         obj?.userData?.onClick?.();
//         return true;
//       };
//     }

//     const dom =
//       camera?.renderer?.domElement || document.querySelector("canvas");
//     dom?.addEventListener("mousemove", handlePointerMove);

//     return () => {
//       dom?.removeEventListener("mousemove", handlePointerMove);
//       cancelAnimationFrame(animationRef.current);
//       clearUI();
//     };
//   }, [
//     scene,
//     camera,
//     modelTemplates,
//     changeScenarioRef,
//     boardSizeRef,
//     GRID_SIZE,
//     font,
//     onClickHandlerRef,
//   ]);

//   return null;
// }

// // import { useEffect, useState } from "react";
// // import * as THREE from "three";
// // import { TTFLoader } from "three/examples/jsm/loaders/TTFLoader.js";
// // import { Font } from "three/examples/jsm/loaders/FontLoader.js";
// // import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";

// // export default function UI3DModelButtons({
// //   scene,
// //   camera,
// //   modelTemplates,
// //   onClickHandlerRef,
// //   changeScenarioRef,
// //   boardSizeRef,
// //   GRID_SIZE,
// // }) {
// //   const [font, setFont] = useState(null);

// //   useEffect(() => {
// //     const loader = new TTFLoader();
// //     loader.load("/fonts/Roboto.ttf", (json) => {
// //       setFont(new Font(json));
// //     });
// //   }, []);

// //   useEffect(() => {
// //     if (!scene || !camera || !modelTemplates || !boardSizeRef || !font) return;

// //     const uiElements = [];
// //     const { dimZ } = boardSizeRef;
// //     if (!dimZ) return;

// //     const y = 0;
// //     const margin = 50;
// //     const centerZ = -(dimZ / 2) * GRID_SIZE;

// //     function clearUI() {
// //       uiElements.forEach((el) => {
// //         scene.remove(el);
// //         el.geometry?.dispose?.();
// //         if (Array.isArray(el.material)) {
// //           el.material.forEach((m) => m.dispose());
// //         } else {
// //           el.material?.dispose?.();
// //         }
// //       });
// //       uiElements.length = 0;
// //     }

// //     function addUIModelButton(
// //       modelName,
// //       offsetZ,
// //       rotationY,
// //       onClick,
// //       label,
// //       arrowColor = 0x294e6b,
// //     ) {
// //       const tpl = modelTemplates[modelName];
// //       if (!tpl) return;

// //       const model = tpl.clone(true);

// //       model.traverse((child) => {
// //         if (child.isMesh) {
// //           child.material = child.material.clone();
// //           child.material.color.set(arrowColor);
// //         }
// //       });

// //       model.scale.set(30, 30, 30);
// //       model.rotation.y = rotationY;
// //       model.position.set(0, y, centerZ + offsetZ);

// //       model.userData.isUI = true;
// //       model.userData.onClick = onClick;

// //       scene.add(model);
// //       uiElements.push(model);

// //       if (label) {
// //         const offsetY = 0.1;
// //         const outwardOffset = 150;
// //         const textOffsetZ = outwardOffset * Math.sign(offsetZ || 1);

// //         const textGeo = new TextGeometry(label, {
// //           font,
// //           size: 5,
// //           height: 1,
// //           curveSegments: 12,
// //           bevelEnabled: true,
// //           bevelThickness: 0.3,
// //           bevelSize: 0.2,
// //           bevelSegments: 3,
// //         });
// //         textGeo.computeBoundingBox();
// //         textGeo.center();

// //         const textMat = new THREE.MeshStandardMaterial({ color: 0x294e6b });
// //         const textMesh = new THREE.Mesh(textGeo, textMat);

// //         textMesh.position.set(
// //           model.position.x,
// //           offsetY,
// //           model.position.z + textOffsetZ,
// //         );
// //         textMesh.rotation.x = -Math.PI / 2;
// //         textMesh.rotation.z = -Math.PI / 2;
// //         textMesh.scale.set(4, 4, 0.05);
// //         textMesh.userData.isUI = true;

// //         scene.add(textMesh);
// //         uiElements.push(textMesh);
// //       }
// //     }

// //     clearUI();

// //     addUIModelButton(
// //       "arrow",
// //       (dimZ / 2) * GRID_SIZE + margin,
// //       -Math.PI / 2,
// //       () => changeScenarioRef?.current?.(1),
// //       "NASTĘPNY",
// //     );

// //     addUIModelButton(
// //       "arrow",
// //       (-dimZ / 2) * GRID_SIZE - margin,
// //       Math.PI / 2,
// //       () => changeScenarioRef?.current?.(-1),
// //       "POPRZEDNI",
// //     );

// //     if (onClickHandlerRef) {
// //       onClickHandlerRef.current = (event) => {
// //         const dom =
// //           camera?.renderer?.domElement || document.querySelector("canvas");
// //         const rect = dom.getBoundingClientRect();
// //         const pointer = new THREE.Vector2(
// //           ((event.clientX - rect.left) / rect.width) * 2 - 1,
// //           -((event.clientY - rect.top) / rect.height) * 2 + 1,
// //         );

// //         const raycaster = new THREE.Raycaster();
// //         raycaster.setFromCamera(pointer, camera);

// //         const intersects = raycaster.intersectObjects(uiElements, true);

// //         if (!intersects.length) return false;

// //         let obj = intersects[0].object;
// //         while (obj && !obj.userData.isUI) obj = obj.parent;

// //         obj?.userData?.onClick?.();
// //         return true;
// //       };
// //     }

// //     return () => {
// //       clearUI();
// //     };
// //   }, [
// //     scene,
// //     camera,
// //     modelTemplates,
// //     changeScenarioRef,
// //     boardSizeRef,
// //     GRID_SIZE,
// //     font,
// //     onClickHandlerRef,
// //   ]);

// //   return null;
// // }

// // import { useEffect } from "react";
// // import * as THREE from "three";

// // export default function UI3DModelButtons({
// //   scene,
// //   camera,
// //   modelTemplates,
// //   onClickHandlerRef,
// //   changeScenarioRef,
// //   boardSizeRef,
// //   GRID_SIZE,
// // }) {

// //   useEffect(() => {
// //     if (!scene || !camera || !modelTemplates || !boardSizeRef) return;

// //     const uiElements = [];
// //     const { dimZ } = boardSizeRef;

// //     if (!dimZ) return;

// //     const y = 0;
// //     const margin = 50;

// //     const centerZ = -(dimZ / 2) * GRID_SIZE;

// //     function addUIModelButton(modelName, offsetZ, rotationY, onClick) {
// //       const tpl = modelTemplates[modelName];
// //       if (!tpl) return;

// //       const model = tpl.clone(true);
// //       model.scale.set(30, 30, 30);
// //       model.rotation.y = rotationY;

// //       model.position.set(0, y, centerZ + offsetZ);

// //       model.userData.isUI = true;
// //       model.userData.onClick = () => {
// //         onClick?.();
// //         model.position.y += 5;
// //         setTimeout(() => (model.position.y -= 5), 100);
// //       };

// //       scene.add(model);
// //       uiElements.push(model);
// //     }

// //     // strzałka do przodu
// //     addUIModelButton(
// //       "arrow",
// //       (dimZ / 2) * GRID_SIZE + margin,
// //       -Math.PI / 2,
// //       () => changeScenarioRef?.current?.(1),
// //     );

// //     // strzałka do tyłu
// //     addUIModelButton(
// //       "arrow",
// //       (-dimZ / 2) * GRID_SIZE - margin,
// //       Math.PI / 2,
// //       () => changeScenarioRef?.current?.(-1),
// //     );

// //     if (onClickHandlerRef) {
// //       onClickHandlerRef.current = (event) => {
// //         const dom =
// //           camera?.renderer?.domElement || document.querySelector("canvas");
// //         const rect = dom.getBoundingClientRect();

// //         const pointer = new THREE.Vector2(
// //           ((event.clientX - rect.left) / rect.width) * 2 - 1,
// //           -((event.clientY - rect.top) / rect.height) * 2 + 1,
// //         );

// //         const raycaster = new THREE.Raycaster();
// //         raycaster.setFromCamera(pointer, camera);

// //         const intersects = raycaster.intersectObjects(uiElements, true);
// //         if (!intersects.length) return false;

// //         let obj = intersects[0].object;
// //         while (obj && !obj.userData.isUI) obj = obj.parent;

// //         obj?.userData?.onClick?.();
// //         return true;
// //       };
// //     }

// //     return () => {
// //       uiElements.forEach((el) => scene.remove(el));
// //     };
// //   }, [
// //     scene,
// //     camera,
// //     modelTemplates,
// //     changeScenarioRef,
// //     boardSizeRef,
// //     GRID_SIZE,
// //   ]);

// //   return null;
// // }

// // import { useEffect } from "react";
// // import * as THREE from "three";
// // import { TTFLoader } from "three/examples/jsm/loaders/TTFLoader.js";
// // import { Font } from "three/examples/jsm/loaders/FontLoader.js";
// // import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";

// // export default function UI3DModelButtons({
// //   scene,
// //   camera,
// //   modelTemplates,
// //   onClickHandlerRef,
// //   changeScenarioRef,
// //   boardSizeRef,
// //   GRID_SIZE,
// // }) {
// //   useEffect(() => {
// //     if (!scene || !camera || !modelTemplates || !boardSizeRef) return;

// //     const uiElements = [];
// //     const { dimZ } = boardSizeRef;
// //     if (!dimZ) return;

// //     const y = 0;
// //     const margin = 50;
// //     const centerZ = -(dimZ / 2) * GRID_SIZE;

// //     const loader = new TTFLoader();

// //     loader.load("/fonts/Roboto.ttf", (json) => {
// //       const font = new Font(json);

// //       function clearUI() {
// //         uiElements.forEach((el) => {
// //           scene.remove(el);
// //           el.geometry?.dispose?.();
// //           if (Array.isArray(el.material)) {
// //             el.material.forEach((m) => m.dispose());
// //           } else {
// //             el.material?.dispose?.();
// //           }
// //         });
// //         uiElements.length = 0;
// //       }

// //       function addUIModelButton(
// //         modelName,
// //         offsetZ,
// //         rotationY,
// //         onClick,
// //         label,
// //         labelOffsetX = 20,
// //       ) {
// //         const tpl = modelTemplates[modelName];
// //         if (!tpl) return;

// //         const model = tpl.clone(true);
// //         model.scale.set(30, 30, 30);
// //         model.rotation.y = rotationY;
// //         model.position.set(0, y, centerZ + offsetZ);

// //         model.userData.isUI = true;
// //         model.userData.onClick = () => {
// //           onClick?.(); // tylko wywołanie zmiany scenariusza
// //           model.position.y += 5;
// //           setTimeout(() => (model.position.y -= 5), 100);
// //         };

// //         scene.add(model);
// //         uiElements.push(model);

// //         if (label) {
// //           const offsetX = labelOffsetX;
// //           const offsetY = 0.1;
// //           const outwardOffset = 150;
// //           const textOffsetZ = outwardOffset * Math.sign(offsetZ || 1);

// //           const textGeo = new TextGeometry(label, {
// //             font,
// //             size: 5,
// //             height: 1,
// //             curveSegments: 12,
// //             bevelEnabled: true,
// //             bevelThickness: 0.3,
// //             bevelSize: 0.2,
// //             bevelSegments: 3,
// //           });

// //           textGeo.computeBoundingBox();
// //           textGeo.center();

// //           const textMat = new THREE.MeshStandardMaterial({ color: 0x294e6b });
// //           const textMesh = new THREE.Mesh(textGeo, textMat);

// //           textMesh.position.set(
// //             model.position.x + offsetX,
// //             offsetY,
// //             model.position.z + textOffsetZ,
// //           );
// //           textMesh.rotation.x = -Math.PI / 2;
// //           textMesh.rotation.z = -Math.PI / 2;
// //           textMesh.scale.set(4, 4, 0.05);
// //           textMesh.userData.isUI = true;

// //           scene.add(textMesh);
// //           uiElements.push(textMesh);
// //         }
// //       }

// //       // Renderowanie przycisków **TYLKO RAZ** po załadowaniu fontu
// //       clearUI();
// //       addUIModelButton(
// //         "arrow",
// //         (dimZ / 2) * GRID_SIZE + margin,
// //         -Math.PI / 2,
// //         () => changeScenarioRef?.current?.(1),
// //         "Następny",
// //         -20,
// //       );
// //       addUIModelButton(
// //         "arrow",
// //         (-dimZ / 2) * GRID_SIZE - margin,
// //         Math.PI / 2,
// //         () => changeScenarioRef?.current?.(-1),
// //         "Poprzedni",
// //         -20,
// //       );

// //       // Kliknięcia
// //       if (onClickHandlerRef) {
// //         onClickHandlerRef.current = (event) => {
// //           const dom =
// //             camera?.renderer?.domElement || document.querySelector("canvas");
// //           if (!dom) return false;

// //           const rect = dom.getBoundingClientRect();
// //           const pointer = new THREE.Vector2(
// //             ((event.clientX - rect.left) / rect.width) * 2 - 1,
// //             -((event.clientY - rect.top) / rect.height) * 2 + 1,
// //           );

// //           const raycaster = new THREE.Raycaster();
// //           raycaster.setFromCamera(pointer, camera);

// //           const intersects = raycaster.intersectObjects(uiElements, true);
// //           if (!intersects.length) return false;

// //           let obj = intersects[0].object;
// //           while (obj && !obj.userData.isUI) obj = obj.parent;

// //           obj?.userData?.onClick?.();
// //           return true;
// //         };
// //       }
// //     });

// //     return () => {
// //       uiElements.forEach((el) => {
// //         scene.remove(el);
// //         el.geometry?.dispose?.();
// //         if (Array.isArray(el.material)) {
// //           el.material.forEach((m) => m.dispose());
// //         } else {
// //           el.material?.dispose?.();
// //         }
// //       });
// //     };
// //   }, [
// //     scene,
// //     camera,
// //     modelTemplates,
// //     changeScenarioRef,
// //     boardSizeRef,
// //     GRID_SIZE,
// //     onClickHandlerRef,
// //   ]);

// //   return null;
// // }

// // import { useEffect } from "react";
// // import * as THREE from "three";

// // export default function UI3DModelButtons({
// //   scene,
// //   camera,
// //   modelTemplates,
// //   onClickHandlerRef,
// //   changeScenarioRef,
// //   boardSizeRef,
// //   GRID_SIZE,
// // }) {

// //   useEffect(() => {
// //     if (!scene || !camera || !modelTemplates || !boardSizeRef) return;

// //     const uiElements = [];
// //     const { dimZ } = boardSizeRef;

// //     if (!dimZ) return;

// //     const y = 0;
// //     const margin = 50;

// //     const centerZ = -(dimZ / 2) * GRID_SIZE;

// //     function addUIModelButton(modelName, offsetZ, rotationY, onClick) {
// //       const tpl = modelTemplates[modelName];
// //       if (!tpl) return;

// //       const model = tpl.clone(true);
// //       model.scale.set(30, 30, 30);
// //       model.rotation.y = rotationY;

// //       model.position.set(0, y, centerZ + offsetZ);

// //       model.userData.isUI = true;
// //       model.userData.onClick = () => {
// //         onClick?.();
// //         model.position.y += 5;
// //         setTimeout(() => (model.position.y -= 5), 100);
// //       };

// //       scene.add(model);
// //       uiElements.push(model);
// //     }

// //     // strzałka do przodu
// //     addUIModelButton(
// //       "arrow",
// //       (dimZ / 2) * GRID_SIZE + margin,
// //       -Math.PI / 2,
// //       () => changeScenarioRef?.current?.(1),
// //     );

// //     // strzałka do tyłu
// //     addUIModelButton(
// //       "arrow",
// //       (-dimZ / 2) * GRID_SIZE - margin,
// //       Math.PI / 2,
// //       () => changeScenarioRef?.current?.(-1),
// //     );

// //     if (onClickHandlerRef) {
// //       onClickHandlerRef.current = (event) => {
// //         const dom =
// //           camera?.renderer?.domElement || document.querySelector("canvas");
// //         const rect = dom.getBoundingClientRect();

// //         const pointer = new THREE.Vector2(
// //           ((event.clientX - rect.left) / rect.width) * 2 - 1,
// //           -((event.clientY - rect.top) / rect.height) * 2 + 1,
// //         );

// //         const raycaster = new THREE.Raycaster();
// //         raycaster.setFromCamera(pointer, camera);

// //         const intersects = raycaster.intersectObjects(uiElements, true);
// //         if (!intersects.length) return false;

// //         let obj = intersects[0].object;
// //         while (obj && !obj.userData.isUI) obj = obj.parent;

// //         obj?.userData?.onClick?.();
// //         return true;
// //       };
// //     }

// //     return () => {
// //       uiElements.forEach((el) => scene.remove(el));
// //     };
// //   }, [
// //     scene,
// //     camera,
// //     modelTemplates,
// //     changeScenarioRef,
// //     boardSizeRef,
// //     GRID_SIZE,
// //   ]);

// //   return null;
// // }

// // import { useEffect } from "react";
// // import * as THREE from "three";
// // export default function UI3DModelButtons({
// //   scene,
// //   camera,
// //   modelTemplates,
// //   objectsRef,
// //   gridWidth,
// //   gridHeight,
// //   onClickHandlerRef,
// //   selectedScenario,
// //   changeScenarioRef,
// //   boardSizeRef,
// //   GRID_SIZE,
// // }) {
// //   useEffect(() => {
// //     if (!scene || !modelTemplates || !camera) return;

// //     const uiElements = [];

// //     const centerZ = (boardSizeRef.dimZ * GRID_SIZE) / 2;

// //     function addUIModelButton(
// //       modelName,
// //       position,
// //       scale = 50,
// //       rotationY = 0,
// //       onClick = null,
// //     ) {
// //       const tpl = modelTemplates[modelName];
// //       if (!tpl) return;

// //       const model = tpl.clone(true);
// //       model.name = modelName;
// //       model.scale.set(scale, scale, scale);
// //       model.rotation.y = rotationY;
// //       model.userData.isUI = true;

// //       model.userData.onClick = () => {
// //         if (onClick) onClick(model);
// //         else console.log(`Kliknięto przycisk: ${modelName}`);

// //         model.position.y += 5;
// //         setTimeout(() => (model.position.y -= 5), 100);
// //       };

// //       model.position.copy(position);
// //       scene.add(model);
// //       uiElements.push(model);
// //     }

// //     const padding = 140;
// //     const y = 0;

// //     console.log("boardSizeRefdsds", boardSizeRef);

// //     addUIModelButton(
// //       "arrow",
// //       //   new THREE.Vector3(-gridWidth / 2 + padding, y, -gridHeight / 2 + padding),
// //       new THREE.Vector3(gridWidth / 2 + padding, y, centerZ),
// //       30,
// //       0,
// //       //   (model) => console.log("Kliknięto prawą strzałkę!"),
// //       () => changeScenarioRef?.current?.(1),
// //     );

// //     addUIModelButton(
// //       "arrow",
// //       //   new THREE.Vector3(-gridWidth / 2 + padding, y, gridHeight / 2 - padding),
// //       new THREE.Vector3(-gridWidth / 2 - padding, y, centerZ),
// //       30,
// //       Math.PI,
// //       //   (model) => console.log("Kliknięto lewą strzałkę!"),
// //       () => changeScenarioRef?.current?.(-1),
// //     );

// //     const raycaster = new THREE.Raycaster();
// //     const pointer = new THREE.Vector2();

// //     if (onClickHandlerRef) {
// //       onClickHandlerRef.current = (event) => {
// //         if (!scene || !camera) return false;

// //         // przelicz współrzędne pointera
// //         const pointer = new THREE.Vector2();
// //         pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
// //         pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

// //         // ustaw raycaster
// //         const raycaster = new THREE.Raycaster();
// //         raycaster.setFromCamera(pointer, camera);

// //         // sprawdzamy wszystkie UI elements
// //         const intersects = raycaster.intersectObjects(uiElements, true);
// //         if (intersects.length > 0) {
// //           let current = intersects[0].object;

// //           // idziemy w górę hierarchii aż znajdziemy root z isUI
// //           while (current && !current.userData.isUI) {
// //             current = current.parent;
// //           }

// //           if (current && current.userData?.onClick) {
// //             current.userData.onClick();
// //             return true; // kliknięto UI
// //           }
// //         }

// //         return false; // kliknięcie nie było w UI
// //       };
// //     }

// //     function onPointerDown(event) {
// //       pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
// //       pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

// //       raycaster.setFromCamera(pointer, camera);

// //       const intersects = raycaster.intersectObjects(uiElements, true);
// //       if (intersects.length > 0) {
// //         let current = intersects[0].object;
// //         while (current && !current.userData.isUI) current = current.parent;
// //         current?.userData?.onClick?.();
// //       }
// //     }

// //     if (scene.userData.rendererDom) {
// //       scene.userData.rendererDom.addEventListener("pointerdown", onPointerDown);
// //     }

// //     return () => {
// //       uiElements.forEach((el) => scene.remove(el));
// //       if (scene.userData.rendererDom)
// //         scene.userData.rendererDom.removeEventListener(
// //           "pointerdown",
// //           onPointerDown,
// //         );
// //     };
// //   }, [
// //     scene,
// //     camera,
// //     modelTemplates,
// //     objectsRef,
// //     selectedScenario?.id,
// //     gridWidth,
// //     gridHeight,
// //   ]);

// //   return null;
// // }
